'use client';

import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import Image from 'next/image';

interface ImageComparisonSliderProps {
  // 左侧图片
  beforeImage: string;
  // 右侧图片
  afterImage: string;
  // 初始位置
  initPosition?: number;
  // 最小位置
  minPosition?: number;
  // 最大位置
  maxPosition?: number;
  // 图片宽度
  width?: number;
  // 图片高度
  height?: number;
  // 左侧图片占位符
  beforePlaceholder?: 'blur' | 'empty';
  // 左侧图片模糊数据URL
  beforeBlurDataURL?: string;
  // 右侧图片占位符
  afterPlaceholder?: 'blur' | 'empty';
  // 右侧图片模糊数据URL
  afterBlurDataURL?: string;
  // 自定义类名
  customClassName?: string;
  // 位置变化回调
  onPositionChange?: (realPosition: number, expectPosition: number) => void;
  showTransparencyGrid?: boolean;
  // 是否显示锁
  isShowLock?: boolean;
}

const ArrowIcon = ({ className = '' }: { className?: string }) => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='10'
      height='17'
      viewBox='0 0 10 17'
      fill='currentColor'
      className={className}
    >
      <path d='M9.03691 16.4445C9.17395 16.3895 9.2911 16.2963 9.37353 16.1767C9.45596 16.0571 9.49997 15.9165 9.5 15.7727L9.5 1.22782C9.50012 1.0839 9.4562 0.943182 9.3738 0.823482C9.2914 0.703782 9.17423 0.610481 9.03712 0.555391C8.90001 0.5003 8.74913 0.485897 8.60358 0.514005C8.45802 0.542114 8.32434 0.611469 8.21946 0.713292L0.71995 7.98573C0.650223 8.05327 0.594907 8.13348 0.557167 8.22176C0.519426 8.31005 0.5 8.40468 0.5 8.50026C0.5 8.59583 0.519426 8.69046 0.557167 8.77875C0.594907 8.86703 0.650223 8.94724 0.71995 9.01478L8.21946 16.2872C8.3244 16.3889 8.45808 16.4581 8.60358 16.4861C8.74909 16.5141 8.89989 16.4996 9.03691 16.4445Z' />
    </svg>
  );
};

const LockIcon = ({ className = '' }: { className?: string }) => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='25'
      height='25'
      viewBox='0 0 25 25'
      fill='currentColor'
      className={className}
    >
      <path d='M20 8H17V5.75C17 4.55653 16.5259 3.41193 15.682 2.56802C14.8381 1.72411 13.6935 1.25 12.5 1.25C11.3065 1.25 10.1619 1.72411 9.31802 2.56802C8.47411 3.41193 8 4.55653 8 5.75V8H5C4.60218 8 4.22064 8.15804 3.93934 8.43934C3.65804 8.72064 3.5 9.10218 3.5 9.5V20C3.5 20.3978 3.65804 20.7794 3.93934 21.0607C4.22064 21.342 4.60218 21.5 5 21.5H20C20.3978 21.5 20.7794 21.342 21.0607 21.0607C21.342 20.7794 21.5 20.3978 21.5 20V9.5C21.5 9.10218 21.342 8.72064 21.0607 8.43934C20.7794 8.15804 20.3978 8 20 8ZM13.25 15.3716V17.75C13.25 17.9489 13.171 18.1397 13.0303 18.2803C12.8897 18.421 12.6989 18.5 12.5 18.5C12.3011 18.5 12.1103 18.421 11.9697 18.2803C11.829 18.1397 11.75 17.9489 11.75 17.75V15.3716C11.2496 15.1946 10.8278 14.8465 10.5593 14.3887C10.2907 13.9309 10.1926 13.3929 10.2824 12.8698C10.3722 12.3466 10.644 11.8721 11.0498 11.53C11.4556 11.1879 11.9692 11.0002 12.5 11.0002C13.0308 11.0002 13.5444 11.1879 13.9502 11.53C14.356 11.8721 14.6278 12.3466 14.7176 12.8698C14.8073 13.3929 14.7093 13.9309 14.4407 14.3887C14.1722 14.8465 13.7504 15.1946 13.25 15.3716ZM15.5 8H9.5V5.75C9.5 4.95435 9.81607 4.19129 10.3787 3.62868C10.9413 3.06607 11.7044 2.75 12.5 2.75C13.2956 2.75 14.0587 3.06607 14.6213 3.62868C15.1839 4.19129 15.5 4.95435 15.5 5.75V8Z' />
    </svg>
  );
};

// 定义透明网格背景样式
const transparencyGridStyle = {
  backgroundImage: `
    linear-gradient(45deg, #ccc 25%, transparent 25%), 
    linear-gradient(-45deg, #ccc 25%, transparent 25%), 
    linear-gradient(45deg, transparent 75%, #ccc 75%), 
    linear-gradient(-45deg, transparent 75%, #ccc 75%)
  `,
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
  backgroundColor: 'white',
};

export interface ImageComparisonSliderHandle {
  releaseDrag: () => void;
}

const ImageComparisonSlider = forwardRef<
  ImageComparisonSliderHandle,
  ImageComparisonSliderProps
>(
  (
    {
      beforeImage,
      afterImage,
      initPosition = 50,
      minPosition = 0,
      maxPosition = 100,
      width = 600,
      height = 400,
      beforePlaceholder,
      beforeBlurDataURL,
      afterPlaceholder,
      afterBlurDataURL,
      customClassName = '',
      onPositionChange,
      showTransparencyGrid = true,
      isShowLock = false,
    }: ImageComparisonSliderProps,
    ref
  ) => {
    const [sliderPosition, setSliderPosition] = useState(initPosition);
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleMove = (event: MouseEvent | TouchEvent) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const x = 'touches' in event ? event.touches[0].clientX : event.clientX;
      const position = ((x - containerRect.left) / containerRect.width) * 100;
      const realPosition = Math.min(
        Math.max(position, minPosition),
        maxPosition
      );
      setSliderPosition(realPosition);
      onPositionChange?.(realPosition, position);
    };

    const handleMouseDown = () => {
      setIsDragging(true);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    useEffect(() => {
      const controller = new AbortController();
      const { signal } = controller;
      document.addEventListener('mousemove', handleMove, { signal });
      document.addEventListener('mouseup', handleMouseUp, { signal });
      document.addEventListener('touchmove', handleMove, { signal });
      document.addEventListener('touchend', handleMouseUp, { signal });

      return () => controller.abort();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging]);

    useEffect(() => {
      onPositionChange?.(initPosition, initPosition);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useImperativeHandle(ref, () => ({
      releaseDrag: () => handleMouseUp(),
    }));

    return (
      <div
        ref={containerRef}
        className={`relative mx-auto w-full max-w-3xl overflow-hidden rounded-lg ${customClassName}`}
        style={{ aspectRatio: `${width}/${height}`, willChange: 'transform' }}
      >
        {/* 透明网格背景 */}
        {showTransparencyGrid && (
          <div className='absolute inset-0' style={transparencyGridStyle} />
        )}
        {/* After Image (左侧) */}
        <div className='absolute inset-0' style={{ willChange: 'opacity' }}>
          <Image
            src={afterImage}
            alt='After'
            width={width}
            height={height}
            className='h-full w-full object-cover'
            placeholder={afterPlaceholder}
            blurDataURL={afterBlurDataURL}
            draggable={false}
          />
        </div>
        {/* Before Image with Clip Path (右侧) */}
        <div
          className='absolute inset-0'
          style={{
            clipPath: `polygon(${sliderPosition}% 0, 100% 0, 100% 100%, ${sliderPosition}% 100%)`,
            willChange: 'clip-path',
          }}
        >
          <Image
            src={beforeImage}
            alt='Before'
            width={width}
            height={height}
            className='h-full w-full object-cover'
            placeholder={beforePlaceholder}
            blurDataURL={beforeBlurDataURL}
            draggable={false}
          />
        </div>
        {/* Slider */}
        <div
          className='absolute inset-y-0'
          style={{ left: `${sliderPosition}%` }}
        >
          <div
            className={`absolute inset-y-0 -ml-0.5 w-1 ${[0, 100].includes(sliderPosition) ? 'opacity-0' : 'opacity-100'} bg-[#f8f8f8] shadow-[0_0_10px_rgba(0,0,0,0.3)]`}
          />
          <button
            className={`absolute top-1/2 size-[3.1875rem] -translate-x-1/2 -translate-y-1/2 ${isShowLock ? 'cursor-grab' : 'cursor-grab'} rounded-full bg-[#f8f8f8] shadow-[0_0_10px_rgba(0,0,0,0.3)] active:cursor-grabbing`}
            onMouseDown={handleMouseDown}
            onTouchStart={handleMouseDown}
            aria-label='Comparison slider'
          >
            <div className='flex items-center justify-center gap-[0.625rem]'>
              <ArrowIcon
                className={`text-[#6137E0] ${isShowLock ? 'hidden' : ''}`}
              />
              <ArrowIcon
                className={`rotate-180 text-[#6137E0] ${isShowLock ? 'hidden' : ''}`}
              />
              <LockIcon
                className={`text-[#6137E0] ${isShowLock ? '' : 'hidden'}`}
              />
            </div>
          </button>
        </div>
      </div>
    );
  }
);

export default ImageComparisonSlider;
